import React from 'react'
import { Link } from 'react-router-dom'

import { Alert, Cluster, Heading, Stack } from 'component-library-next'
import { RiFilterLine } from 'react-icons/ri'

import { useIntl } from '@app/intl/Intl'
import { Pagination } from '@app/lib/components/Pagination/Pagination'
import { ProductType } from '@app/lib/display/ProductType/ProductType'
import { usePersistedState } from '@app/lib/hooks/usePersistedState'
import { Button } from '@app/lib/inputs/Button/Button'
import { Card } from '@app/lib/layout/Card/Card'
import { CardBlock } from '@app/lib/layout/Card/CardBlock'
import { CardHeader } from '@app/lib/layout/Card/CardHeader'
import { LineItem } from '@app/lib/layout/LineItem/LineItem'
import { Sidebar } from '@app/lib/layout/Sidebar/Sidebar'
import { Table, TableColumn } from '@app/lib/layout/Table/Table'
import { Text } from '@app/lib/typography/Text/Text'
import { CustomerKeywordFilterForm } from '@app/modules/CustomerList/components/KeywordSearch/CustomerKeywordFilterForm'
import { CustomerFilterForm } from '@app/modules/CustomerList/CustomerFilterForm'
import {
  CustomerItem,
  useCustomerListTable
} from '@app/modules/CustomerList/hooks/useCustomerListTable'
import { getMessagesPath } from '@app/modules/Messages'

export const CustomerListWidget = () => {
  const { formatMessage } = useIntl()
  const [showFilter, setShowFilter] = usePersistedState(`customer-filter`, false)

  const { data, loading, error, query, dispatch } = useCustomerListTable()

  const columns: Array<TableColumn<CustomerItem>> = [
    {
      key: 'customerFullName',
      label: formatMessage({ id: 'Lawyer.CustomerList.Table.Column.Name' }),
      sort: query.sorts.lastName,
      onSortChange: (direction) => dispatch({ type: 'SET_SORT', payload: { lastName: direction } }),
      value: (customer) => (
        <Link to={getMessagesPath(customer.id)}>
          {[customer.firstName, customer.lastName].filter(Boolean).join(' ')}
        </Link>
      )
    },
    {
      key: 'membershipNumber',
      label: formatMessage({ id: 'Lawyer.CustomerList.Table.Column.MembershipNumber' }),
      value: (customer) => customer.membership?.membershipNumber
    },
    {
      key: 'productName',
      label: formatMessage({ id: 'Lawyer.CustomerList.Table.Column.ProductName' }),
      value: (customer) => (
        <ProductType
          uid={customer.membership?.membershipType.uid}
          displayName={
            customer.membership?.membershipType.displayName ||
            customer.currentSingleProductPurchase?.product?.name
          }
        />
      )
    },
    {
      key: 'id',
      label: formatMessage({ id: 'Lawyer.CustomerList.Table.Column.Id' }),
      sort: query.sorts.id,
      onSortChange: (direction) => dispatch({ type: 'SET_SORT', payload: { id: direction } }),
      value: (customer) => customer.id
    }
  ]

  return (
    <Card>
      <CardHeader>
        <LineItem
          label={
            <Heading size={Heading.sizes.small}>
              {formatMessage({ id: 'Lawyer.CustomerList.Heading' })}
            </Heading>
          }
          actions={
            <Cluster>
              <Button size='sm' variant='outlined' onClick={() => setShowFilter((prev) => !prev)}>
                <RiFilterLine />
                <span>
                  {formatMessage({
                    id: showFilter
                      ? 'Lawyer.CustomerList.Table.HideFilter'
                      : 'Lawyer.CustomerList.Table.ShowFilter'
                  })}
                </span>
              </Button>
            </Cluster>
          }
        />
      </CardHeader>
      {error && (
        <CardBlock>
          <Alert color={Alert.colors.warning}>
            {formatMessage({ id: 'Lawyer.CustomerList.Table.Error' })}
          </Alert>
        </CardBlock>
      )}
      <Sidebar
        sidebar={
          <CustomerFilterForm
            values={query.filters}
            onSubmit={(data) => dispatch({ type: 'SET_FILTER', payload: data })}
            onReset={() => dispatch({ type: 'RESET' })}
          />
        }
        isVisible={showFilter}
      >
        <Stack>
          <CustomerKeywordFilterForm
            values={query.filters}
            onSubmit={(data) => dispatch({ type: 'SET_FILTER', payload: data })}
          />
          <Table
            columns={columns}
            items={data?.customers.items || []}
            loading={loading}
            noResults={formatMessage({ id: 'Lawyer.CustomerList.Table.NoResults' })}
          />
          <Pagination
            value={{ ...query.pagination, total: data?.customers.total || 0 }}
            onChange={(data) => dispatch({ type: 'SET_PAGINATION', payload: data })}
          />
          <Text color='primary' weight='bold'>
            {formatMessage(
              { id: 'Lawyer.CustomerList.Pagination.TotalEntries' },
              {
                totalEntries: data?.customers.total
              }
            )}
          </Text>
        </Stack>
      </Sidebar>
    </Card>
  )
}
